import React from 'react';
import { createPortal } from 'react-dom';
import type { ContactResponse } from '@troia-v3/types';
import { formatPhoneNumber } from '@/shared/utils';
import { Icon } from '@/shared/icons'
import { PrivacyText } from '@/shared/components/ui/PrivacyText'
interface DeleteContactDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (id: string) => void;
  contact: ContactResponse | null;
  isLoading?: boolean;
}

/**
 * Dialog de confirmação para deletar contato
 *
 * @example
 * <DeleteContactDialog
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onConfirm={handleDeleteContact}
 *   contact={selectedContact}
 * />
 */
const DeleteContactDialog: React.FC<DeleteContactDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  contact,
  isLoading = false,
}) => {
  const handleConfirm = () => {
    if (contact) {
      onConfirm(contact.id);
    }
  };

  if (!isOpen || !contact) return null;

  const contactName = contact.name || 'este contato';
  const contactPhone = contact.identifiers.phone[0] ? formatPhoneNumber(contact.identifiers.phone[0]) : 'N/A';

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center z-[60] p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
    >
      <div
        className="rounded-lg shadow-xl max-w-md w-full"
        style={{ backgroundColor: '#ffffff' }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-4 border-b"
          style={{ borderColor: '#e5e7eb' }}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <Icon name="alert-triangle" className="w-5 h-5 text-red-600" />
            </div>
            <h2
              className="text-lg font-semibold"
              style={{ color: '#1e293b' }}
            >
              Deletar Contato
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded transition-colors"
            style={{ color: '#64748b' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            disabled={isLoading}
          >
            <Icon name="x" className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="mb-4" style={{ color: '#1e293b' }}>
            Tem certeza que deseja deletar <span className="font-semibold">{contactName}</span>?
          </p>

          <div
            className="p-3 rounded-lg mb-4"
            style={{
              backgroundColor: '#f1f5f9',
              borderColor: '#e5e7eb',
            }}
          >
            <p className="text-sm" style={{ color: '#64748b' }}>
              <span className="font-medium">Telefone:</span> <PrivacyText>{contactPhone}</PrivacyText>
            </p>
            {contact.identifiers.email?.[0] && (
              <p className="text-sm mt-1" style={{ color: '#64748b' }}>
                <span className="font-medium">Email:</span> <PrivacyText>{contact.identifiers.email[0]}</PrivacyText>
              </p>
            )}
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-800">
              <span className="font-semibold">Atenção:</span> Esta ação não pode ser desfeita.
              Todas as informações do contato serão permanentemente removidas.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex gap-3 p-4 border-t"
          style={{ borderColor: '#e5e7eb' }}
        >
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 text-sm font-medium rounded-lg border disabled:opacity-50"
            style={{
              color: '#1e293b',
              backgroundColor: '#ffffff',
              borderColor: '#e5e7eb',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ffffff';
            }}
            disabled={isLoading}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading ? 'Deletando...' : 'Deletar Contato'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DeleteContactDialog;
